import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { productGroups } from "../../mock";
import { useLanguage } from "../../contexts/LanguageContext";
import { useQuoteCart } from "../../contexts/QuoteCartContext";
import { ChevronRight, ChevronUp, ChevronDown, Share2, FileText, Copy, Check, Mail } from "lucide-react";
import CategoryPage from "./CategoryPage";
import { t } from "../../i18n";
import { getProductName, translateSeriesName, translateSpecKey, translateSpecValue } from "../../utils/translator";
import { trackGoogleAdsConversion } from "../../utils/googleAds";

const ProductDetailPage = ({ product: propProduct, category: propCategory }) => {
    const params = useParams();
    const { lang } = useLanguage();
    const navigate = useNavigate();
    const { addToCart } = useQuoteCart();
    const [quantity, setQuantity] = useState(1);
    const [isZoomed, setIsZoomed] = useState(false);
    
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copied, setCopied] = useState(false);
    const shareMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
                setShowShareMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const [product, setProduct] = useState(propProduct || null);
    const [category, setCategory] = useState(propCategory || null);
    const [allCategoryProducts, setAllCategoryProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const categorySlug = params.slug || params.categorySlug || propCategory?.slug;
    const { subSlug, subSubSlug, productId } = params;
    const actualId = productId || subSubSlug || subSlug || propProduct?.id;

    useEffect(() => {
        setLoading(true);
        setError(false);
        
        const cat = propCategory || productGroups.find((g) => g.slug === categorySlug);
        setCategory(cat);

        if (categorySlug) {
            fetch(`/data/products/${categorySlug}.json`)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to load product data");
                    }
                    return res.json();
                })
                .then((data) => {
                    setAllCategoryProducts(data);
                    
                    const activeId = actualId || (propProduct ? propProduct.id : null);
                    const prod = data.find((p) => String(p.id) === String(activeId));
                    if (prod) {
                        setProduct(prod);
                    } else if (propProduct) {
                        setProduct(propProduct);
                    } else {
                        setError(true);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Error loading category products:", err);
                    if (propProduct) {
                        setProduct(propProduct);
                        setLoading(false);
                    } else {
                        setError(true);
                        setLoading(false);
                    }
                });
        } else {
            setError(true);
            setLoading(false);
        }
    }, [propProduct, propCategory, categorySlug, actualId]);

    const getSubcategorySlug = (prod) => {
        if (!prod.subcategory) return "";
        const subCat = category?.subCategories?.find(
            sc => sc.title_tr === prod.subcategory || sc.title_en === prod.subcategory || sc.slug === prod.subcategory.toLowerCase().replace(/\s+/g, '-')
        );
        return subCat ? subCat.slug : prod.subcategory.toLowerCase().replace(/\s+/g, '-');
    };

    const handleOptionClick = (opt) => {
        const subSlug = getSubcategorySlug(opt);
        const targetUrl = `/products/${category.slug}/${subSlug ? subSlug + '/' : ''}${opt.id}`;
        navigate(targetUrl);
    };

    const optionProducts = useMemo(() => {
        if (categorySlug !== "self-servis" || !product || allCategoryProducts.length === 0) {
            return [];
        }

        const siblings = allCategoryProducts.filter(
            (p) => p.subcategory === product.subcategory && String(p.id) !== String(product.id)
        );

        const selectedSiblings = siblings.slice(0, 8);
        const optionsList = [product, ...selectedSiblings];

        if (optionsList.length < 9) {
            const others = allCategoryProducts.filter(
                (p) => p.subcategory !== product.subcategory && String(p.id) !== String(product.id)
            );
            const remainingCount = 9 - optionsList.length;
            optionsList.push(...others.slice(0, remainingCount));
        }

        return optionsList.slice(0, 9);
    }, [categorySlug, product, allCategoryProducts]);

    const handleZoomClose = () => {
        setIsZoomed(false);
    };

    if (loading) {
        return (
            <div className="pt-[140px] bg-white min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    <p className="text-gray-500 text-sm tracking-widest uppercase font-bold animate-pulse">
                        {lang === "tr" ? "Ürün Yükleniyor..." : "Loading Product..."}
                    </p>
                </div>
            </div>
        );
    }

    if (error || !product || !category) {
        return <CategoryPage />;
    }

    const breadcrumbs = [
        { title: t(lang, "nav.home"), path: "/" },
        { title: t(lang, "nav.products"), path: "/products" },
        { title: category[`title_${lang}`], path: `/products/${category.slug}` },
        { title: getProductName(product, lang), path: null }
    ];

    return (
        <div className="pt-[140px] bg-white min-h-screen pb-20">
            {/* Breadcrumbs */}
            <div className="bg-gray-50 border-b border-gray-100 py-4 mb-10">
                <div className="mx-auto max-w-7xl px-4 flex items-center space-x-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    {breadcrumbs.map((bc, index) => (
                        <React.Fragment key={index}>
                            {bc.path ? (
                                <Link to={bc.path} className="hover:text-primary transition-colors">
                                    {bc.title}
                                </Link>
                            ) : (
                                <span className="text-primary">{bc.title}</span>
                            )}
                            {index < breadcrumbs.length - 1 && (
                                <ChevronRight size={12} className="text-gray-300" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4">
                <div className="flex flex-col lg:flex-row gap-16">
                    {/* Left: Image Gallery */}
                    <div className="w-full lg:w-1/2">
                        {categorySlug === 'self-servis' && optionProducts.length > 0 ? (
                            <div className="flex flex-row gap-4 items-start">
                                {/* Thumbnail Column */}
                                <div className="flex flex-col space-y-2 pr-2 select-none overflow-y-auto max-h-[500px] w-20 flex-shrink-0 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                                    {optionProducts.map((opt) => (
                                        <button
                                            key={opt.id}
                                            onClick={() => handleOptionClick(opt)}
                                            className={`w-16 h-16 bg-white border-2 rounded-xl overflow-hidden p-1 flex-shrink-0 transition-all ${
                                                String(opt.id) === String(product.id)
                                                    ? 'border-primary shadow-sm scale-95'
                                                    : 'border-gray-100 hover:border-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={opt.imageUrl}
                                                alt={getProductName(opt, lang)}
                                                className="w-full h-full object-contain"
                                            />
                                        </button>
                                    ))}
                                </div>
                                {/* Main Image */}
                                <div 
                                    className="flex-grow bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden group cursor-zoom-in relative"
                                    onClick={() => setIsZoomed(true)}
                                >
                                    <img
                                        src={product.imageUrl}
                                        alt={getProductName(product, lang)}
                                        className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                                </div>
                            </div>
                        ) : (
                            <div 
                                className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden group cursor-zoom-in relative"
                                onClick={() => setIsZoomed(true)}
                            >
                                <img
                                    src={product.imageUrl}
                                    alt={getProductName(product, lang)}
                                    className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                            </div>
                        )}
                    </div>

                    {/* Right: Product Info */}
                    <div className="w-full lg:w-1/2 flex flex-col pt-4">
                        <div className="mb-6">
                            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full mb-4">
                                {lang === "tr" ? product.series : translateSeriesName(product.series, lang)}
                            </span>
                            <h1 className="text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
                                {getProductName(product, lang)}
                            </h1>
                            <p className="text-gray-400 text-sm font-medium tracking-widest uppercase">
                                SKU: {product.id}
                            </p>
                        </div>

                        <div className="h-px bg-gray-100 w-full mb-8" />

                        <div className="flex flex-col space-y-6 mb-8">
                            <div className="flex items-start space-x-3">
                                <div className="mt-1 bg-primary/10 p-1.5 rounded-lg text-primary">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-1">
                                        {lang === "tr" ? "ÜRÜN AÇIKLAMASI" : "PRODUCT DESCRIPTION"}
                                    </h4>
                                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                                        {lang === "tr" 
                                            ? (product.description_tr || "Endüstriyel mutfak standartlarına uygun, yüksek kaliteli malzemelerden üretilmiştir. Uzun ömürlü kullanım ve maksimum verimlilik sağlar.")
                                            : (product.description_en || "Manufactured with high-quality materials according to industrial kitchen standards. Ensures long-lasting use and maximum efficiency.")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {product.specs && Object.keys(product.specs).length > 0 && (
                            <div className="mb-10">
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 border-b border-gray-100 pb-2">
                                    {lang === "tr" ? "TEKNİK ÖZELLİKLER" : "TECHNICAL SPECIFICATIONS"}
                                </h4>
                                <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <tbody>
                                            {Object.entries(product.specs).map(([key, val], idx) => {
                                                const translatedKey = translateSpecKey(key, lang);
                                                const translatedVal = translateSpecValue(val, lang);
                                                return (
                                                    <tr key={key} className={idx % 2 === 0 ? "bg-gray-50/50" : "bg-white"}>
                                                        <td className="px-4 py-3 font-semibold text-gray-500 w-1/3 border-b border-gray-100/50">{translatedKey}</td>
                                                        <td className="px-4 py-3 text-gray-950 font-medium border-b border-gray-100/50">{translatedVal}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row items-center gap-4 mt-auto">
                            <div className="flex items-center bg-gray-50 rounded-2xl p-1 border border-gray-100">
                                <button 
                                    className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-white rounded-xl"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                >
                                    <ChevronDown size={20} />
                                </button>
                                <span className="w-12 text-center font-bold text-gray-900">{quantity}</span>
                                <button 
                                    className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-white rounded-xl"
                                    onClick={() => setQuantity(quantity + 1)}
                                >
                                    <ChevronUp size={20} />
                                </button>
                            </div>
                            <button 
                                onClick={() => {
                                    trackGoogleAdsConversion('quote_request_intent', {
                                        product_id: product?.id,
                                        product_name: product?.[`name_${lang}`],
                                        quantity: quantity
                                    });
                                    addToCart(product, quantity);
                                    navigate("/teklif-sepeti");
                                }}
                                className="flex-1 w-full bg-primary text-white font-bold py-5 px-8 rounded-2xl shadow-lg shadow-primary/30 hover:bg-primary/90 transition-all transform hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest text-sm"
                            >
                                {lang === "tr" ? "TEKLİF AL" : "GET A QUOTE"}
                            </button>
                            <div className="relative" ref={shareMenuRef}>
                                <button 
                                    onClick={() => setShowShareMenu(!showShareMenu)}
                                    className="w-14 h-14 flex items-center justify-center bg-gray-50 text-gray-400 hover:text-primary border border-gray-100 rounded-2xl transition-colors"
                                    title={lang === "tr" ? "Paylaş" : "Share"}
                                >
                                    <Share2 size={20} />
                                </button>
                                {showShareMenu && (
                                    <div className="absolute right-0 bottom-16 bg-white border border-gray-100 rounded-2xl shadow-xl p-3 w-56 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 py-1 mb-2">
                                            {lang === "tr" ? "PAYLAŞ" : "SHARE PRODUCT"}
                                        </div>
                                        <div className="space-y-1">
                                            {/* WhatsApp */}
                                            <button 
                                                onClick={() => {
                                                    trackGoogleAdsConversion('whatsapp_click', {
                                                        source: 'product_share',
                                                        product_id: product?.id,
                                                        product_name: product?.[`name_${lang}`]
                                                    });
                                                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${product[`name_${lang}`]} - ${window.location.href}`)}`, "_blank");
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <svg className="h-4 w-4 fill-[#25D366]" viewBox="0 0 24 24">
                                                    <path d="M12.004 2C6.51 2 2.014 6.5 2.014 12c0 2.18.7 4.21 2.006 5.85L2.3 22l4.314-1.39A9.972 9.972 0 0012.004 22c5.494 0 9.99-4.5 9.99-10S17.498 2 12.004 2zM12 20.37a8.318 8.318 0 01-4.24-1.16l-.3-.18-2.54.82.83-2.48-.2-.31a8.347 8.347 0 01-1.28-4.47c0-4.61 3.75-8.36 8.35-8.36 4.6 0 8.35 3.75 8.35 8.36 0 4.61-3.75 8.36-8.35 8.36zm4.72-6.12c-.26-.13-1.53-.76-1.77-.84-.24-.09-.41-.13-.58.13-.17.26-.67.84-.82 1.01-.15.17-.3.2-.56.07A7.054 7.054 0 0110.1 12.8a7.781 7.781 0 01-1.99-2.48c-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.46.13-.15.17-.26.26-.43.08-.17.04-.32-.02-.45-.06-.13-.58-1.39-.79-1.9-.21-.5-.42-.43-.58-.44h-.5c-.17 0-.45.06-.69.32a2.637 2.637 0 00-.82 1.95c0 1.28.93 2.52 1.06 2.7.13.17 1.83 2.8 4.43 3.93.62.27 1.1.43 1.48.55.62.2 1.19.17 1.64.1.5-.07 1.53-.63 1.74-1.23.21-.6.21-1.12.15-1.23-.07-.11-.25-.2-.51-.33z"/>
                                                </svg>
                                                <span>WhatsApp</span>
                                            </button>

                                            {/* Telegram */}
                                            <button 
                                                onClick={() => {
                                                    window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`${product[`name_${lang}`]} - Profinoks`)}`, "_blank");
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <svg className="h-4 w-4 fill-[#0088cc]" viewBox="0 0 24 24">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.24-5.54 3.65-.52.36-.99.54-1.41.53-.46-.01-1.35-.26-2.01-.48-.81-.27-1.46-.42-1.4-.89.03-.25.38-.51 1.07-.78 4.2-1.83 7-3.04 8.4-3.63 4-.16 4.83 1.25 4.8 2.15z"/>
                                                </svg>
                                                <span>Telegram</span>
                                            </button>

                                            {/* Facebook */}
                                            <button 
                                                onClick={() => {
                                                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank");
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <svg className="h-4 w-4 fill-[#1877F2]" viewBox="0 0 24 24">
                                                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z"/>
                                                </svg>
                                                <span>Facebook</span>
                                            </button>

                                            {/* Gmail */}
                                            <button 
                                                onClick={() => {
                                                    window.open(`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=&su=${encodeURIComponent(`${product[`name_${lang}`]} - Profinoks`)}&body=${encodeURIComponent(window.location.href)}`, "_blank");
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <svg className="h-4 w-4" viewBox="0 0 24 24">
                                                    <path fill="#EA4335" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 6l8 5 8-5v2l-8 5-8-5V6zm0 12V8.7l8 5 8-5V18H4z"/>
                                                </svg>
                                                <span>Gmail</span>
                                            </button>

                                            {/* E-posta */}
                                            <button 
                                                onClick={() => {
                                                    window.open(`mailto:?subject=${encodeURIComponent(`${product[`name_${lang}`]} - Profinoks`)}&body=${encodeURIComponent(window.location.href)}`, "_self");
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <Mail size={16} className="text-gray-600" />
                                                <span>{lang === "tr" ? "E-posta" : "Email"}</span>
                                            </button>

                                            {/* Instagram */}
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(window.location.href);
                                                    setCopied(true);
                                                    setTimeout(() => setCopied(false), 2000);
                                                    setShowShareMenu(false);
                                                    alert(lang === "tr" ? "Ürün bağlantısı kopyalandı! Instagram'da paylaşmak için mesajlara veya hikayenize yapıştırabilirsiniz." : "Product link copied! You can paste it into messages or stories to share on Instagram.");
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                            >
                                                <svg className="h-4 w-4 text-pink-600 fill-none stroke-current" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                                                </svg>
                                                <span>Instagram</span>
                                            </button>

                                            {/* Copy Link */}
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(window.location.href);
                                                    setCopied(true);
                                                    setTimeout(() => setCopied(false), 2000);
                                                    setShowShareMenu(false);
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors border-t border-gray-100 mt-1 pt-2"
                                            >
                                                {copied ? (
                                                    <>
                                                        <Check size={16} className="text-green-600" />
                                                        <span className="text-green-600 font-bold">{lang === "tr" ? "Kopyalandı!" : "Copied!"}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={16} className="text-gray-600" />
                                                        <span>{lang === "tr" ? "Bağlantıyı Kopyala" : "Copy Link"}</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Zoom Overlay */}
            {isZoomed && (
                <div 
                    className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in transition-all"
                    onClick={handleZoomClose}
                >
                    <div className="max-w-7xl max-h-screen">
                        <img
                            src={product.imageUrl}
                            alt="Zoomed Product"
                            className="w-full h-auto max-h-[90vh] object-contain rounded-2xl"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetailPage;
